// components/AddHorasRegaloConfigModal.tsx
import React, { useState, useEffect } from 'react';
import { createHorasRegaloConfig } from '../../services/horasRegaloConfig';
import { fetchPrecioPlanesServicios, PrecioPlanServicio } from '../../services/PrecioPlanServicio';

interface AddHorasRegaloConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (newConfig: any) => void;
}

const AddConfigHoras: React.FC<AddHorasRegaloConfigModalProps> = ({ isOpen, onClose, onCreated }) => {
    const [formData, setFormData] = useState({
        precio_plan_servicio_id: 0,
        horas_asignadas: '' as number | string,
        precios_plan_permitidos: [] as number[],  // Cambiado
        activo: true,
    });
    const [precioPlanesServicios, setPrecioPlanesServicios] = useState<PrecioPlanServicio[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadPrecioPlanesServicios();
            setFormData({
                precio_plan_servicio_id: 0,
                horas_asignadas: '',
                precios_plan_permitidos: [],
                activo: true,
            });
            setError('');
        }
    }, [isOpen]);

    const loadPrecioPlanesServicios = async () => {
        try {
            const precioPlanesData = await fetchPrecioPlanesServicios();
            setPrecioPlanesServicios(precioPlanesData.filter(pps => pps.estado));
        } catch (err) {
            /* Browser diagnostics omitted. */
            setError('Error al cargar los precios de plan-servicio');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        
        if (name === 'horas_asignadas') {
            if (value === '' || /^\d*$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'number' || name === 'precio_plan_servicio_id' ? Number(value) : value
            }));
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handlePrecioPlanPermitidoChange = (id: number) => {
        setFormData(prev => {
            const isSelected = prev.precios_plan_permitidos.includes(id);
            return {
                ...prev,
                precios_plan_permitidos: isSelected
                    ? prev.precios_plan_permitidos.filter(x => x !== id)
                    : [...prev.precios_plan_permitidos, id]
            };
        });
    };

    const formatPrecioPlanServicioName = (pps: PrecioPlanServicio) => {
        return `${pps.servicio_nombre} - ${pps.plan_nombre}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.precio_plan_servicio_id === 0) {
            setError('Seleccione una combinación de Plan-Servicio');
            return;
        }
        
        const horasAsignadas = formData.horas_asignadas === '' ? 0 : Number(formData.horas_asignadas);
        if (horasAsignadas <= 0) {
            setError('Las horas asignadas deben ser mayores a 0');
            return;
        }

        if (!formData.precios_plan_permitidos.length) {
            setError('Selecciona los servicios y planes donde se podrán usar las horas.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const dataToSend = {
                ...formData,
                horas_asignadas: horasAsignadas
            };
            const newConfig = await createHorasRegaloConfig(dataToSend);
            onCreated(newConfig);
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.message || 'Error al crear la configuración');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Nueva Configuración de Horas de Regalo</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Servicio y plan que otorgan las horas *
                            </label>
                            <select
                                name="precio_plan_servicio_id"
                                value={formData.precio_plan_servicio_id}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={0}>Seleccionar Plan - Servicio</option>
                                {precioPlanesServicios.map(pps => (
                                    <option key={pps.id} value={pps.id}>{formatPrecioPlanServicioName(pps)}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Horas Asignadas *</label>
                            <input
                                type="number"
                                name="horas_asignadas"
                                value={formData.horas_asignadas}
                                onChange={handleChange}
                                min={1}
                                placeholder="Ej: 5"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Servicios y planes donde se pueden usar las horas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                            {precioPlanesServicios
                                .filter(pps => pps.id !== formData.precio_plan_servicio_id)
                                .map(pps => (
                                    <label key={pps.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                                        <input
                                            type="checkbox"
                                            checked={formData.precios_plan_permitidos.includes(pps.id)}
                                            onChange={() => handlePrecioPlanPermitidoChange(pps.id)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">
                                            {formatPrecioPlanServicioName(pps)}
                                        </span>
                                    </label>
                                ))}
                        </div>
                        <p className="text-sm mt-1 text-gray-500">
                            {formData.precios_plan_permitidos.length === 0
                                ? 'Selecciona al menos un servicio y plan con espacio físico para usar las horas'
                                : `${formData.precios_plan_permitidos.length} precio(s) plan-servicio permitido(s)`}
                        </p>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                            <span className="block text-sm font-medium text-gray-700 mb-1">Estado de la Configuración</span>
                            <p className="text-xs text-gray-500">{formData.activo ? 'Visible y activo' : 'Oculto e inactivo'}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="activo"
                                checked={formData.activo}
                                onChange={handleCheckboxChange}
                                className="sr-only"
                            />
                            <div className={`w-10 h-6 rounded-full transition-colors ${formData.activo ? 'bg-[#c1348b]' : 'bg-gray-300'}`}>
                                <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.activo ? 'translate-x-5' : 'translate-x-0'}`}></div>
                            </div>
                        </label>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg font-medium transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg font-medium flex items-center justify-center min-w-[120px] transition-colors"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                    </svg>
                                    Guardando...
                                </span>
                            ) : 'Crear Configuración'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddConfigHoras;
